import { debounce, parseInt } from 'lodash';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  getAllElementsByCssStylesRecursive,
  getAllElementStyleValues,
  getElementByCssStylesRecursive,
  isEventOutsideTarget,
} from '../../common-functions';
import { DomHandler } from '../../dom-handler';
import { DomElementAlignment, DomTargetPosition } from '../../enums';
import { bindDocumentClickListener, unbindDocumentClickListener } from '../../event-handlers';
import OdinTransition from '../odin-transition/odin-transition.component';
import OdinIcon from '../../odin-icon/odin-icon.component';
import clsx from 'clsx';
import type { TypeOrArray } from '@beesoft/common';
import type { ConstrainedDimensions, OverlayPanelPosition } from './overlay-panel.props';
import { OverlayScrollDetectionType } from './overlay-panel.props';
import { useSharedTestIdContext } from '../../../common/hooks/use-shared-test-id';
import useTestTag from '../../../common/hooks/use-test-tag';
import { combineRefs } from '../../../common/functions/combine-refs';

export interface OverlayPanelProps {
  visible: boolean;
  // the globalThis type below is a TypeScript reference to the global object...this is needed to identify this is the
  // DOM MouseEvent not React's version.
  target?: MouseEvent<Element, globalThis.MouseEvent> | HTMLElement | Element | null;
  targetPosition?: DomTargetPosition;
  elementAlignment?: DomElementAlignment;
  /**
   * If this is set to false then clicking the target will not close the overlay.
   */
  shouldTargetCloseOverlay?: boolean;
  /**
   * If this is set to true then the overlay will make its width the same as the target passed to it (as long as the
   * target is bigger than itself).
   */
  shouldMatchTargetWidth?: boolean;
  shouldScrollCloseOverlay?: boolean;
  shouldCheckZIndex?: boolean;
  /**
   * When set to true this will cause the overlay panel to adjust itself, relative to its target, to remain on the
   * screen.
   */
  shouldRemainOnScreen?: boolean;
  shouldUseCreatePortal?: boolean;
  /**
   * When true this will not mount the components until the transition is activated.
   */
  mountOnEnter?: boolean;
  /**
   * When true this will unmount the component once the transition is closed.
   */
  unmountOnExit?: boolean;
  scrollDetection?: OverlayScrollDetectionType;
  appendTo?: HTMLElement;
  transitionDuration?: number;
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  className?: string;
  shown?: () => void;
  hidden?: () => void;
  onMouseEnter?: () => void;
  children: ReactNode;
  header?: string | React.JSX.Element;
  showCloseIcon?: boolean;
  /**
   * When shouldMatchTargetWidth is false and you still want the minWidth to match the target width
   */
  isTargetWidthMinWidth?: boolean;
  keepOverlayOpen?: boolean;
}

export default function OverlayPanel({
  visible,
  target,
  targetPosition = DomTargetPosition.TopLeft,
  elementAlignment = DomElementAlignment.TopLeft,
  shouldTargetCloseOverlay = true,
  shouldMatchTargetWidth = false,
  shouldScrollCloseOverlay = false,
  shouldCheckZIndex = false,
  shouldRemainOnScreen = false,
  shouldUseCreatePortal = true,
  mountOnEnter = true,
  unmountOnExit = true,
  scrollDetection = OverlayScrollDetectionType.Parent,
  appendTo = document.body,
  transitionDuration = 400,
  showTransitionOptions = 'cubic-bezier(0, 0, 0.2, 1)',
  hideTransitionOptions = 'linear',
  className = '',
  shown,
  hidden,
  onMouseEnter,
  children,
  showCloseIcon,
  header,
  isTargetWidthMinWidth = false,
  keepOverlayOpen = false,
}: OverlayPanelProps) {
  const [zIndex, setZIndex] = useState(-1);
  const [displayZIndex, setDisplayZIndex] = useState(100);
  const [visibility, setVisibility] = useState(visible);
  const [dimensionsChangedFlag, setDimensionsChangedFlag] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement>();

  const dimensionsChangedRef = useRef(false);
  const documentElement = useRef<Element>(document.scrollingElement || document.documentElement);
  const panelRef = useRef<HTMLDivElement>();
  const scrollerPanelRef = useRef<TypeOrArray<HTMLElement>>();
  const finalTarget = useRef<HTMLElement>();
  // the globalThis type below is a TypeScript reference to the global object...this is needed to identify this is the
  // DOM MouseEvent not React's version.
  const listenerRef = useRef<(event: globalThis.MouseEvent) => void>();
  const scrollListenerRef = useRef<(event: Event) => void>();
  const resizeObserver = useRef<ResizeObserver>();
  const targetResizeObserver = useRef<ResizeObserver>();

  const panelPosition = useRef<OverlayPanelPosition>({
    top: 0,
    left: 0,
    width: 0,
  });

  const sharedTestData = useSharedTestIdContext();
  const tagCreator = useTestTag(sharedTestData?.sharedId || '');

  // the globalThis type below is a TypeScript reference to the global object...this is needed to identify this is the
  // DOM MouseEvent not React's version.
  const getHTMLElement = useCallback((target: MouseEvent<Element, globalThis.MouseEvent> | HTMLElement | Element) => {
    return (
      (target as MouseEvent<Element, globalThis.MouseEvent>).target
        ? (target as MouseEvent<Element, globalThis.MouseEvent>).target
        : target
    ) as HTMLElement;
  }, []);

  useLayoutEffect(() => {
    if (shouldUseCreatePortal && target) {
      const currentTargetElement = getHTMLElement(target);
      finalTarget.current = currentTargetElement;

      const dialogAncestor = finalTarget.current?.closest('dialog');
      if (dialogAncestor) {
        setPortalTarget(dialogAncestor);
      } else {
        setPortalTarget(appendTo || document.body);
      }
    }
  }, [target, appendTo, shouldUseCreatePortal, getHTMLElement]);

  useEffect(() => {
    if (shouldRemainOnScreen === true) {
      resizeObserver.current = new ResizeObserver(resizeCallback);
    }

    targetResizeObserver.current = new ResizeObserver(targetResizeCallback);

    // Capture refs for cleanup
    const currentResizeObserver = resizeObserver.current;
    const currentTargetResizeObserver = targetResizeObserver.current;
    const currentPanel = panelRef.current;

    return () => {
      if (shouldRemainOnScreen === true) {
        if (currentPanel) {
          currentResizeObserver?.unobserve(currentPanel);
        }

        currentResizeObserver?.disconnect();
      }

      currentTargetResizeObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only effect for setting up resize observers
  }, []);

  useEffect(() => {
    if (target) {
      finalTarget.current = getHTMLElement(target);

      if (shouldScrollCloseOverlay) {
        // eslint rule no-extra-boolean-cast: direct truthiness check
        const validElement = (element: HTMLElement) => !element.dataset.skipScrollElement;
        scrollerPanelRef.current =
          scrollDetection === OverlayScrollDetectionType.Parent
            ? getElementByCssStylesRecursive(
                finalTarget.current,
                {
                  overflow: 'scroll, auto',
                  overflowX: 'scroll, auto',
                  overflowY: 'scroll, auto',
                },
                false,
                validElement
              )
            : getAllElementsByCssStylesRecursive(
                finalTarget.current,
                {
                  overflow: 'scroll, auto',
                  overflowX: 'scroll, auto',
                  overflowY: 'scroll, auto',
                },
                false,
                validElement
              );
      }

      targetResizeObserver.current?.observe(finalTarget.current);
    }

    return () => {
      if (finalTarget.current) {
        targetResizeObserver.current?.unobserve(finalTarget.current);
      }
    };
  }, [target, shouldScrollCloseOverlay, getHTMLElement, scrollDetection]);

  useEffect(() => {
    if (visible && shouldCheckZIndex) {
      const allZIndexes = getAllElementStyleValues('zIndex', (styleValue) => {
        const elementZIndex = parseInt(styleValue);
        return elementZIndex >= 100;
      }).map((item) => parseInt(item));

      if (allZIndexes.length > 0) {
        setDisplayZIndex(Math.max(...allZIndexes) + 1);
      }
    }
  }, [visible, shouldCheckZIndex]);

  const visibleRef = useRef(visible);

  useEffect(() => {
    visibleRef.current = visible;
    if (visible !== undefined) {
      setVisibility(visible);
    }
  }, [visible]);

  const positionToTarget = useCallback(
    (fireDimensionsChanged = false) => {
      if (finalTarget.current && panelRef.current) {
        const position = DomHandler.positionElementToTarget(
          panelRef.current,
          finalTarget.current,
          targetPosition,
          elementAlignment
        );

        panelPosition.current = {
          ...(panelPosition.current.constrainedDimensions
            ? panelPosition.current
            : { left: position.left, width: finalTarget.current.offsetWidth }),
          top: position.top,
        };

        if (fireDimensionsChanged) {
          onDimensionsChanged();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDimensionsChanged is debounced and defined after this callback
    [targetPosition, elementAlignment]
  );

  const onPanelRefCreated = useCallback(
    (panelElement: HTMLDivElement) => {
      if (shouldRemainOnScreen === true && panelRef.current) {
        resizeObserver.current?.unobserve(panelRef.current);
      }

      panelRef.current = panelElement;
      positionToTarget();

      if (shouldRemainOnScreen === true) {
        resizeObserver.current?.observe(panelRef.current);
      }
    },
    [positionToTarget, shouldRemainOnScreen]
  );

  const targetResizeCallback = () => {
    positionToTarget(true);
  };

  const resizeCallback = (entries: Array<ResizeObserverEntry>) => {
    if (panelRef.current) {
      const windowSize = DomHandler.getScreenDimensions();
      const overlayElement = entries[entries.length - 1].target as HTMLElement;
      const overlayRectangle = overlayElement.getBoundingClientRect();
      const offScreenLocation = DomHandler.determineOffScreenLocation(overlayRectangle);

      if (offScreenLocation) {
        if (
          finalTarget.current &&
          DomHandler.canPositionElementOnScreenWithTarget(overlayElement, finalTarget.current)
        ) {
          const newDimensions = DomHandler.positionElementToTargetOnScreen(
            panelRef.current,
            finalTarget.current,
            elementAlignment,
            targetPosition
          );

          panelPosition.current = {
            ...panelPosition.current,
            ...newDimensions,
          };
          onDimensionsChanged();
        } else {
          const constrainedDimensions: ConstrainedDimensions = {};
          const computedStyle = window.getComputedStyle(panelRef.current);

          if (offScreenLocation.right || offScreenLocation.left) {
            const paddingRight = parseFloat(computedStyle.paddingRight);
            const paddingLeft = parseFloat(computedStyle.paddingLeft);
            panelPosition.current.left = paddingRight;

            if (windowSize.width < overlayRectangle.width) {
              constrainedDimensions.width = windowSize.width - paddingLeft - paddingRight;
            }
          }

          if (offScreenLocation.bottom || offScreenLocation.top) {
            const paddingBottom = parseFloat(computedStyle.paddingBottom);
            const paddingTop = parseFloat(computedStyle.paddingTop);

            panelPosition.current.top = paddingBottom;

            if (windowSize.height < overlayRectangle.height) {
              constrainedDimensions.height = windowSize.height - paddingTop - paddingBottom;
            }
          }

          if (Object.keys(constrainedDimensions).length > 0) {
            panelPosition.current.constrainedDimensions = constrainedDimensions;
          }

          onDimensionsChanged();
        }
      }
    }
  };

  const onDimensionsChanged = debounce(() => {
    dimensionsChangedRef.current = !dimensionsChangedRef.current;
    setDimensionsChangedFlag(dimensionsChangedRef.current);
  }, 20);

  const handleCloseClick = useCallback(() => {
    setVisibility(false);
  }, []);

  const renderHeader = useCallback(() => {
    if (header || showCloseIcon) {
      const closeButtonStyles = header
        ? '-odin-mr-2 -odin-mt-1 odin-flex odin-shrink odin-cursor-pointer odin-items-start odin-pl-2 odin-text-primary-2'
        : 'odin-absolute odin-right-1 odin-top-1 odin-cursor-pointer odin-text-primary-2';
      const headerStyles = clsx('odin-grow odin-truncate odin-text-base odin-font-medium odin-text-mono-0', {
        'odin-mb-2': header,
      });
      return (
        <div className="odin-items-top odin-flex odin-shrink">
          <div className={headerStyles}>{header}</div>
          {showCloseIcon && (
            <div className={closeButtonStyles} onClick={handleCloseClick}>
              <OdinIcon icon="X" />
            </div>
          )}
        </div>
      );
    }
  }, [header, showCloseIcon, handleCloseClick]);

  const onEntering = () => {
    setZIndex(displayZIndex);
  };

  const onEntered = () => {
    if (!panelRef.current) return;

    // Guard against race condition: if visible changes from true→false before the enter animation
    // completes, onEntered could fire after the component has already started exiting. Without this
    // check, listeners would be set up but never cleaned up (since onExit expects them to exist).
    if (!visibleRef.current) {
      return;
    }

    shown?.();

    let otherElements: Array<HTMLElement> | undefined = undefined;
    let ignoreElements: Array<HTMLElement> | undefined = undefined;
    if (!shouldTargetCloseOverlay) {
      const targetElement = target as HTMLElement;
      otherElements = [targetElement];
      ignoreElements = Array.from(targetElement.querySelectorAll('[data-target-ignore]'));
    }

    const clickListener = (clickedWithin: boolean) => {
      if (!clickedWithin && !keepOverlayOpen) {
        setVisibility(false);
      }
    };
    listenerRef.current = bindDocumentClickListener(panelRef.current, clickListener, otherElements, ignoreElements);

    if (shouldScrollCloseOverlay) {
      scrollListenerRef.current = (event: Event) => {
        if (panelRef.current && isEventOutsideTarget(event, panelRef.current)) {
          setVisibility(false);
        }
      };
      if (scrollerPanelRef.current && scrollListenerRef.current) {
        if (!Array.isArray(scrollerPanelRef.current)) {
          scrollerPanelRef.current?.addEventListener('scroll', scrollListenerRef.current);
        } else {
          const scrollListener = scrollListenerRef.current;
          scrollerPanelRef.current.map((element: HTMLElement) => {
            if (documentElement.current.tagName !== element.tagName) {
              element.addEventListener('scroll', scrollListener);
            } else {
              window.addEventListener('scroll', scrollListener);
            }
          });
        }
      }
    }
  };

  const onExit = () => {
    // Guard against race conditions: only clean up if listeners were actually set up.
    // If visible changed to false before onEntered completed, listeners may not exist yet.
    if (!listenerRef.current && !scrollListenerRef.current) {
      return;
    }

    if (listenerRef.current) {
      unbindDocumentClickListener(listenerRef.current);
      listenerRef.current = undefined;
    }

    if (scrollerPanelRef.current && scrollListenerRef.current) {
      if (!Array.isArray(scrollerPanelRef.current)) {
        scrollerPanelRef.current.removeEventListener('scroll', scrollListenerRef.current);
      } else {
        const scrollListener = scrollListenerRef.current;
        scrollerPanelRef.current.map((element: HTMLElement) => {
          if (documentElement.current.tagName !== element.tagName) {
            element.removeEventListener('scroll', scrollListener);
          } else {
            window.removeEventListener('scroll', scrollListener);
          }
        });
      }

      scrollListenerRef.current = undefined;
    }
  };

  const onExited = () => {
    setZIndex(-1);
    // Call hidden() after exit animation completes.
    // This fires for all hide triggers: click-away, scroll, close icon, or parent visible prop change.
    hidden?.();
  };

  const baseStyles: CSSProperties = useMemo(() => {
    const constrainedWidth = panelPosition.current?.constrainedDimensions?.width;
    const constrainedHeight = panelPosition.current?.constrainedDimensions?.height;

    const styles: CSSProperties = {
      top: `${panelPosition.current.top}px`,
      left: `${panelPosition.current.left}px`,
      zIndex,
      ...(constrainedWidth && { width: `${constrainedWidth}px`, overflowX: 'auto' }),
      ...(constrainedHeight && { height: `${constrainedHeight}px`, overflowY: 'auto' }),
    };

    if (shouldMatchTargetWidth) {
      styles['width'] = `${panelPosition.current.width}px`;
      styles['minWidth'] = '200px';
    } else if (isTargetWidthMinWidth) {
      styles['minWidth'] = `${panelPosition.current.width}px`;
    }
    return styles;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dimensionsChangedFlag triggers recalculation when panel is repositioned
  }, [dimensionsChangedFlag, zIndex, shouldMatchTargetWidth, isTargetWidthMinWidth]);

  const panelCallbackRef = (element: HTMLDivElement | null) => {
    if (element) {
      onPanelRefCreated(element);
    }
  };
  const combinedRef = combineRefs(panelCallbackRef, tagCreator('overlay-panel'));

  const createElement = () => (
    <OdinTransition
      ref={combinedRef}
      start={visibility}
      timeout={transitionDuration}
      showTransitionOptions={showTransitionOptions}
      hideTransitionOptions={hideTransitionOptions}
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExited={onExited}
      onMouseEnter={onMouseEnter}
      className={`odin-fixed odin-shadow custom-scroll ${className}`}
      style={baseStyles}
    >
      {renderHeader()}
      {children}
    </OdinTransition>
  );

  // createPortal allows a piece of markup to be rendered outside of the React root node but operate like it is inside
  // of the root node...in this components case since we are using absolute positioning having the markup appended to a
  // high level node like the body is the best way to allow positioning to be accurate.
  if (shouldUseCreatePortal) {
    if (!portalTarget) return null;

    return ReactDOM.createPortal(createElement(), portalTarget);
  }

  return createElement();
}
